// src/services/firestoreSchemaService.js
/**
 * Firestore Schema Service
 * Handles schema migrations and updates for enhanced beverage data models
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  writeBatch,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase-config";
import { baseBeverageSchema } from "../models/beverageModels";

export class FirestoreSchemaService {
  constructor() {
    this.schemaVersion = "2.0.0";
    this.migrationCollection = "schema_migrations";
  }

  /**
   * Check if schema migration is needed
   * @param {string} restaurantId - Restaurant ID
   */
  async checkMigrationStatus(restaurantId) {
    try {
      const migrationRef = doc(db, this.migrationCollection, restaurantId);
      const migrationSnap = await getDoc(migrationRef);

      if (!migrationSnap.exists()) {
        return {
          needsMigration: true,
          currentVersion: "1.0.0",
          targetVersion: this.schemaVersion,
          reason: "No migration record found",
        };
      }

      const migrationData = migrationSnap.data();
      const needsMigration = migrationData.schemaVersion !== this.schemaVersion;

      return {
        needsMigration,
        currentVersion: migrationData.schemaVersion,
        targetVersion: this.schemaVersion,
        lastMigration: migrationData.migratedAt,
        reason: needsMigration ? "Schema version mismatch" : "Up to date",
      };
    } catch (error) {
      console.error("Error checking migration status:", error);
      return {
        needsMigration: true,
        currentVersion: "unknown",
        targetVersion: this.schemaVersion,
        reason: "Error checking status",
      };
    }
  }

  /**
   * Migrate restaurant's beverage collection to new schema
   * @param {string} restaurantId - Restaurant ID
   * @param {object} options - Migration options
   */
  async migrateRestaurantSchema(restaurantId, options = {}) {
    const {
      backupData = true,
      forceUpdate = false,
      batchSize = 50,
      onProgress = null,
    } = options;

    try {
      console.log(
        `🔧 Starting schema migration for restaurant: ${restaurantId}`
      );

      // Check if migration is needed
      const migrationStatus = await this.checkMigrationStatus(restaurantId);
      if (!migrationStatus.needsMigration && !forceUpdate) {
        console.log("✅ Schema is already up to date");
        return {
          success: true,
          message: "Already up to date",
          migratedItems: 0,
        };
      }

      // Get all menu items for this restaurant
      const menuItemsQuery = query(
        collection(db, "menu_items"),
        where("restaurantId", "==", restaurantId)
      );

      const snapshot = await getDocs(menuItemsQuery);
      const totalItems = snapshot.size;
      console.log(`📊 Found ${totalItems} items to migrate`);

      if (totalItems === 0) {
        await this.recordMigrationCompletion(restaurantId, {
          migratedItems: 0,
        });
        return {
          success: true,
          message: "No items to migrate",
          migratedItems: 0,
        };
      }

      // Backup existing data if requested
      if (backupData) {
        await this.backupExistingData(restaurantId, snapshot.docs);
      }

      // Process items in batches
      const batches = this.createBatches(snapshot.docs, batchSize);
      let totalMigrated = 0;

      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        console.log(`🔄 Processing batch ${i + 1}/${batches.length}`);

        const batchResult = await this.migrateBatch(batch);
        totalMigrated += batchResult.migrated;

        if (onProgress) {
          onProgress(totalMigrated, totalItems);
        }
      }

      // Record migration completion
      await this.recordMigrationCompletion(restaurantId, {
        migratedItems: totalMigrated,
        totalItems,
      });

      console.log(
        `✅ Schema migration completed: ${totalMigrated}/${totalItems} items migrated`
      );
      return {
        success: true,
        message: "Migration completed successfully",
        migratedItems: totalMigrated,
        totalItems,
      };
    } catch (error) {
      console.error("Error during schema migration:", error);
      throw error;
    }
  }

  /**
   * Migrate a batch of documents
   * @param {Array} docBatch - Array of document snapshots
   */
  async migrateBatch(docBatch) {
    const batch = writeBatch(db);
    let migrated = 0;

    for (const docSnap of docBatch) {
      try {
        const oldData = docSnap.data();
        const newData = this.transformToNewSchema(oldData);

        batch.update(docSnap.ref, newData);
        migrated++;
      } catch (error) {
        console.error(`Error migrating document ${docSnap.id}:`, error);
      }
    }

    await batch.commit();
    return { migrated };
  }

  /**
   * Transform old schema data to new enhanced schema
   * @param {object} oldData - Old document data
   */
  transformToNewSchema(oldData) {
    // Start with the base schema
    const newData = {
      ...baseBeverageSchema,
      ...oldData, // Preserve existing data

      // Add new fields with defaults
      aiEnhanced: false,
      aiEnhancedAt: null,
      aiVersion: null,
      schemaVersion: this.schemaVersion,
      migratedAt: serverTimestamp(),

      // Initialize AI enhancement structure if not present
      enhancement: oldData.enhancement || null,

      // Ensure category is standardized
      category: this.standardizeCategory(oldData.category),

      // Initialize pairing data structure
      pairings: oldData.pairings || {
        foodPairings: [],
        autoPairings: [],
        customPairings: [],
        confidence: null,
        lastUpdated: null,
      },

      // Initialize rating aggregation
      ratings: oldData.ratings || {
        professional: [],
        customer: [],
        average: null,
        count: 0,
      },

      // Initialize availability tracking
      availability: oldData.availability || {
        inStock: oldData.inStock !== undefined ? oldData.inStock : true,
        quantity: null,
        lastUpdated: serverTimestamp(),
        stockAlerts: false,
      },

      // Initialize analytics
      analytics: oldData.analytics || {
        views: 0,
        orders: 0,
        pairingsRequested: 0,
        popularityScore: 0,
        lastAnalyzed: null,
      },
    };

    // Clean up undefined values
    Object.keys(newData).forEach((key) => {
      if (newData[key] === undefined) {
        delete newData[key];
      }
    });

    return newData;
  }

  /**
   * Standardize beverage categories
   * @param {string} category - Original category
   */
  standardizeCategory(category) {
    if (!category) return "unknown";

    const categoryMap = {
      wine: "wine",
      wines: "wine",
      "red wine": "wine",
      "white wine": "wine",
      rosé: "wine",
      sparkling: "wine",
      champagne: "wine",

      beer: "beer",
      beers: "beer",
      ale: "beer",
      lager: "beer",
      stout: "beer",
      ipa: "beer",

      spirit: "spirit",
      spirits: "spirit",
      whiskey: "spirit",
      whisky: "spirit",
      vodka: "spirit",
      gin: "spirit",
      rum: "spirit",
      tequila: "spirit",
      brandy: "spirit",
      cognac: "spirit",

      cocktail: "cocktail",
      cocktails: "cocktail",
      "mixed drink": "cocktail",
      "mixed drinks": "cocktail",

      mocktail: "mocktail",
      mocktails: "mocktail",
      "non-alcoholic": "mocktail",

      sake: "sake",
      cider: "cider",
      mead: "mead",

      coffee: "coffee",
      tea: "tea",
      "soft drink": "soft_drink",
      soda: "soft_drink",
      juice: "soft_drink",
    };

    const normalized = category.toLowerCase().trim();
    return categoryMap[normalized] || "other";
  }

  /**
   * Backup existing data before migration
   * @param {string} restaurantId - Restaurant ID
   * @param {Array} docs - Document snapshots to backup
   */
  async backupExistingData(restaurantId, docs) {
    try {
      console.log(`💾 Creating backup of ${docs.length} items`);

      const backupData = {
        restaurantId,
        backupCreatedAt: serverTimestamp(),
        schemaVersion: "1.0.0",
        itemCount: docs.length,
        items: docs.map((doc) => ({
          id: doc.id,
          data: doc.data(),
        })),
      };

      const backupRef = doc(
        db,
        "schema_backups",
        `${restaurantId}_${Date.now()}`
      );
      await setDoc(backupRef, backupData);

      console.log("✅ Backup created successfully");
    } catch (error) {
      console.error("Error creating backup:", error);
      throw error;
    }
  }

  /**
   * Record migration completion
   * @param {string} restaurantId - Restaurant ID
   * @param {object} migrationData - Migration results
   */
  async recordMigrationCompletion(restaurantId, migrationData) {
    try {
      const migrationRef = doc(db, this.migrationCollection, restaurantId);
      await setDoc(migrationRef, {
        restaurantId,
        schemaVersion: this.schemaVersion,
        migratedAt: serverTimestamp(),
        ...migrationData,
      });
    } catch (error) {
      console.error("Error recording migration completion:", error);
    }
  }

  /**
   * Create batches from array
   * @param {Array} array - Array to batch
   * @param {number} batchSize - Size of each batch
   */
  createBatches(array, batchSize) {
    const batches = [];
    for (let i = 0; i < array.length; i += batchSize) {
      batches.push(array.slice(i, i + batchSize));
    }
    return batches;
  }

  /**
   * Initialize new collections for enhanced features
   * @param {string} restaurantId - Restaurant ID
   */
  async initializeEnhancedCollections(restaurantId) {
    try {
      console.log(
        `🔧 Initializing enhanced collections for restaurant: ${restaurantId}`
      );

      // Initialize food menu collection
      const foodMenuRef = doc(db, "food_menus", restaurantId);
      await setDoc(
        foodMenuRef,
        {
          restaurantId,
          status: "not_uploaded",
          createdAt: serverTimestamp(),
          dishes: [],
          totalDishes: 0,
        },
        { merge: true }
      );

      // Initialize pairing suggestions collection
      const pairingRef = doc(db, "pairing_suggestions", `${restaurantId}_auto`);
      await setDoc(
        pairingRef,
        {
          restaurantId,
          generatedAt: null,
          totalPairings: 0,
          pairings: [],
          version: "1.0",
        },
        { merge: true }
      );

      // Initialize menu analysis collection
      const analysisRef = doc(db, "menu_analysis", `${restaurantId}_full`);
      await setDoc(
        analysisRef,
        {
          restaurantId,
          menuType: "full",
          parsedAt: null,
          totalDishes: 0,
          dishes: [],
          analysisLevel: "basic",
        },
        { merge: true }
      );

      // Initialize beverage enhancements collection
      const enhancementRef = doc(db, "beverage_enhancements", restaurantId);
      await setDoc(
        enhancementRef,
        {
          restaurantId,
          totalBeverages: 0,
          enhancedBeverages: 0,
          lastEnhanced: null,
          enhancementSettings: {
            autoEnhance: false,
            enhancementLevel: "basic",
            apiSources: [],
          },
        },
        { merge: true }
      );

      console.log("✅ Enhanced collections initialized");
    } catch (error) {
      console.error("Error initializing enhanced collections:", error);
      throw error;
    }
  }

  /**
   * Rollback schema migration
   * @param {string} restaurantId - Restaurant ID
   * @param {string} backupId - Backup document ID
   */
  async rollbackMigration(restaurantId, backupId) {
    try {
      console.log(`🔄 Rolling back migration for restaurant: ${restaurantId}`);

      // Get backup data
      const backupRef = doc(db, "schema_backups", backupId);
      const backupSnap = await getDoc(backupRef);

      if (!backupSnap.exists()) {
        throw new Error("Backup not found");
      }

      const backupData = backupSnap.data();

      // Restore data in batches
      const batches = this.createBatches(backupData.items, 50);

      for (const batch of batches) {
        const batchWriter = writeBatch(db);

        for (const item of batch) {
          const docRef = doc(db, "menu_items", item.id);
          batchWriter.set(docRef, item.data);
        }

        await batchWriter.commit();
      }

      // Update migration record
      const migrationRef = doc(db, this.migrationCollection, restaurantId);
      await updateDoc(migrationRef, {
        schemaVersion: backupData.schemaVersion,
        rolledBackAt: serverTimestamp(),
        rolledBackFrom: this.schemaVersion,
      });

      console.log("✅ Migration rollback completed");
    } catch (error) {
      console.error("Error rolling back migration:", error);
      throw error;
    }
  }

  /**
   * Get migration history for a restaurant
   * @param {string} restaurantId - Restaurant ID
   */
  async getMigrationHistory(restaurantId) {
    try {
      const migrationRef = doc(db, this.migrationCollection, restaurantId);
      const migrationSnap = await getDoc(migrationRef);

      if (!migrationSnap.exists()) {
        return null;
      }

      return migrationSnap.data();
    } catch (error) {
      console.error("Error getting migration history:", error);
      return null;
    }
  }
}

export const firestoreSchemaService = new FirestoreSchemaService();
